import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import ReactMarkdown from 'react-markdown';
import { styled, Theme, useTheme } from '../../styles';
import { AppText, Subheader } from '../typography';
import { Button, IconButton } from '../buttons';
import { Row } from '../layout';
import remarkGfm from 'remark-gfm';
import { NoteSuccessfullySent } from './note_successfully_sent';
const MAPPED_COMPONENTS = {
    div: ({ ...props }) => _jsx(ReactMarkdownDiv, { ...props }),
    h1: ({ ...props }) => _jsx(ReactMarkdownH1, { ...props }),
    h2: ({ ...props }) => _jsx(ReactMarkdownH2, { ...props }),
    h3: ({ ...props }) => _jsx(ReactMarkdownH3, { ...props }),
    h4: ({ ...props }) => _jsx(ReactMarkdownH4, { ...props }),
    h5: ({ ...props }) => _jsx(ReactMarkdownH5, { ...props }),
    h6: ({ ...props }) => _jsx(ReactMarkdownH6, { ...props }),
    ul: ({ ...props }) => _jsx(ReactMarkdownUL, { ...props }),
    ol: ({ ...props }) => _jsx(ReactMarkdownOL, { ...props }),
    p: ({ ...props }) => _jsx(ReactMarkdownP, { ...props }),
    span: () => _jsx(ReactMarkdownSpan, {}),
    strong: ({ ...props }) => _jsx(ReactMarkdownStrong, { ...props }),
    a: ({ ...props }) => {
        const linkWithBlankTarget = { ...props, target: '_blank' };
        return _jsx(ReactMarkdownA, { ...linkWithBlankTarget });
    },
    em: ({ ...props }) => _jsx(ReactMarkdownEM, { ...props }),
};
export const MarkDownNotesWithButton = ({ text, onEdit, showTitle, readOnly = false, editButtonStyles = {}, handlePrepareToSendNote, finalizedAtDate, recordTooltipHover, notReadyToFinalizeNote, }) => {
    const theme = useTheme();
    const isDisabled = text.length === 0 || finalizedAtDate != null || notReadyToFinalizeNote;
    const sendNote = () => {
        handlePrepareToSendNote();
    };
    return (_jsxs("div", { "data-testid": "markdown-call-notes-view", "data-clarity-mask": "true", children: [_jsx(Header, { "data-clarity-unmask": "true", children: showTitle && (_jsxs(SendNoteContainer, { children: [_jsx(Subheader, { size: "mediumLarge", "data-testid": "call-notes-heading", children: "Call Notes" }), !readOnly &&
                            (finalizedAtDate ? (_jsx(NoteSuccessfullySent, { recordTooltipHover: recordTooltipHover })) : (_jsx(Button, { "data-testid": "open-send-note-modal-button", variant: "secondary", onClick: sendNote, fontColor: isDisabled ? 'grey40' : 'blue50', fontSize: "small", style: {
                                    width: '5.625rem',
                                    height: '2.125rem',
                                    borderColor: isDisabled
                                        ? Theme.colors.grey40
                                        : Theme.colors.blue50,
                                }, disabled: isDisabled, children: "Send Note" })))] })) }), _jsx("div", { "data-clarity-unmask": "true", children: !readOnly && (_jsxs("div", { style: { display: 'flex', flexDirection: 'row', alignItems: 'center' }, children: [_jsx(IconButton, { iconName: "EditIcon", name: "editNotes", label: "Edit Notes", onClick: onEdit, style: {
                                ...editButtonStyles,
                            }, fill: theme.colors.azure, "data-testid": "edit-notes-icon" }), _jsx(AppText, { onClick: onEdit, semibold: true, as: "span", style: { color: theme.colors.azure, cursor: 'pointer' }, children: "Edit" })] })) }), _jsx(Markdown, { text: text })] }));
};
export const Markdown = ({ text }) => {
    return (_jsx(ReactMarkdown, { components: MAPPED_COMPONENTS, remarkPlugins: [remarkGfm], children: text }));
};
const Header = styled(Row)({
    textAlign: 'left',
});
const ReactMarkdownDiv = styled.div({
    textAlign: 'left',
});
const ReactMarkdownH1 = styled.h1(({ theme }) => ({
    fontWeight: 'bolder',
    fontSize: '22px',
    color: theme.colors.grey80,
    fontFamily: 'Poppins',
    overflowWrap: 'break-word',
}));
const ReactMarkdownH2 = styled.h2(({ theme }) => ({
    fontWeight: 'bolder',
    fontSize: '20px',
    color: theme.colors.grey80,
    fontFamily: 'Poppins',
    overflowWrap: 'anywhere',
}));
const ReactMarkdownH3 = styled.h3(({ theme }) => ({
    fontWeight: 'bolder',
    fontSize: '18px',
    color: theme.colors.grey80,
    fontFamily: 'Poppins',
    overflowWrap: 'break-word',
}));
const ReactMarkdownH4 = styled.h4(({ theme }) => ({
    fontWeight: 'bolder',
    fontSize: '16px',
    color: theme.colors.grey80,
    fontFamily: 'Poppins',
    overflowWrap: 'break-word',
}));
const ReactMarkdownH5 = styled.h5(({ theme }) => ({
    fontWeight: 'bolder',
    fontSize: '14px',
    color: theme.colors.grey80,
    fontFamily: 'Poppins',
    overflowWrap: 'break-word',
}));
const ReactMarkdownH6 = styled.h6(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: '13px',
    color: theme.colors.grey80,
    fontFamily: 'Poppins',
    overflowWrap: 'break-word',
}));
const ReactMarkdownUL = styled('ul')({
    marginTop: '1em',
    marginBottom: '1em',
    marginLeft: '2em',
    listStyle: 'circle outside none',
});
const ReactMarkdownOL = styled('ol')({
    marginTop: '1em',
    marginBottom: '1em',
    marginLeft: '2em',
    listStyleType: 'decimal',
});
const ReactMarkdownSpan = styled.span(({ theme }) => ({
    color: theme.colors.grey90,
    fontFamily: 'ASAP',
    fontSize: '16px',
    lineHeight: '1.5',
}));
const ReactMarkdownP = styled.p(({ theme }) => ({
    display: 'block',
    color: theme.colors.grey90,
    fontFamily: 'ASAP',
    fontSize: '16px',
    lineHeight: '1.5',
    overflowWrap: 'anywhere',
}));
const ReactMarkdownStrong = styled.strong(({ theme }) => ({
    fontWeight: 600,
    fontFamily: 'ASAP',
    fontSize: '16px',
    color: theme.colors.grey90,
}));
const ReactMarkdownA = styled.a(({ theme }) => ({
    color: theme.colors.blue50,
    fontFamily: 'Poppins',
    fontSize: '16px',
    textDecoration: 'none',
    fontWeight: 600,
    overflowWrap: 'break-word',
    ':hover': {
        color: theme.colors.blue40,
    },
}));
const ReactMarkdownEM = styled.em(({ theme }) => ({
    fontFamily: 'ASAP',
    fontStyle: 'italic',
    color: theme.colors.grey90,
}));
const SendNoteContainer = styled(Row)({
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
});
//# sourceMappingURL=markdown_notes_with_button.js.map